import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { InitialInterviewPage, InitialInterviewQuestionType, PayTransparencyCategory } from "../../../enums";
import {ActivatedRoute, Router} from "@angular/router";
import { QuestionService } from "../service/question-service";
import { PageService } from "../service/page-service";
import { CustomQuestion, InitialInterviewQuestion, OrderPayTransparencyData } from "../../../../typings";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { InitialInterviewApiService } from "../initial-interview-api.service";
import { timeout, map, catchError, shareReplay, takeUntil } from "rxjs/operators";
import { Observable, of, ReplaySubject } from "rxjs";
import { combineQuestions, isNumbersEqual } from '../util';
import { PreInterviewQuestionsService } from "../../../pre-interview-questions/pre-interview-questions.service";
import { TranslateService } from "@ngx-translate/core";
import { QuickApplyApiService } from "../../quick-apply/quick-apply-api.service";
import { SharedDataService } from "../../../gather/gather-services/shared-data.service";
import { GatherApiService } from "../../../gather/gather-services/gather-api.service";
import { Country } from "../../../gather/enums";
import { FeatureService, TalentAppFeatures } from "../../../service/feature.service";
import { PayTransparencyService } from "../service/payTransparency-service";
import { VideoInterviewFlowService } from "../video-interview/service/video-interview-flow.service";

@Component({
  selector: 'app-initial-interview-container',
  standalone: false,
  templateUrl: './initial-interview-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './initial-interview-container.component.scss'
})
export class InitialInterviewContainerComponent implements OnInit, OnDestroy {
  timeout_duration = 180000;
  activePage = InitialInterviewPage.APPLICATION_SUBMIT_PAGE;
  currentQuestionIndex = 1;
  posting: PostingInfo;
  initialInterviewQuestions: InitialInterviewQuestion[] = [];
  standardQuestionIds: number[] = [];
  returningCandidate = false;
  questionsLoading = false;
  gatherUUID: string;
  returningGather: boolean = false;
  postingInfo: PostingInfo;

  /**
   * The one video-interview-fields read per entry into this module, shared by everything that waits
   * on it. Held here so that every waiter subscribes to the same instance, which is what makes it
   * one request; `shareReplay(1)` then hands the settled answer to a waiter that arrives after it.
   */
  private videoInterviewFields: Observable<unknown>;

  /**
   * How long the video interview fields read may take. It gates the first paint of every entry into
   * this module that re-reads them — all but a gather link opened from its e-mail, which reads the
   * whole posting under `timeout_duration` instead — so it is short, unlike `timeout_duration`, which
   * is three minutes because it also bounds question generation. A read that runs out fails closed
   * like any other failure: the fields are cleared, so the step is not offered on this entry, and the
   * funnel carries on.
   */
  private readonly videoInterviewFieldsTimeoutMs = 15000;

  /**
   * Emits when this entry into the module ends. The video interview fields read is cancelled then,
   * because `postingInfo` and `PageService` are root-scoped and outlive this component: a read still
   * in flight when the talent leaves would otherwise write the fields, and run the navigation
   * waiting on it, into whatever entry comes next. A `ReplaySubject` so that a read first asked for
   * after the entry has ended is never started at all.
   */
  private readonly destroyed = new ReplaySubject<void>(1);

  constructor(private route: ActivatedRoute,
              private router: Router,
              private questionService: QuestionService,
              private pageService: PageService,
              private talentHappyPathDataService: TalentHappyPathDataService,
              private initialInterviewApiService: InitialInterviewApiService,
              private preInterviewQuestionsService: PreInterviewQuestionsService,
              private quickApplyService: QuickApplyApiService,
              private sharedDataService: SharedDataService,
              private gatherApiService: GatherApiService,
              private translateService: TranslateService,
              private featureService: FeatureService,
              private payTransparencyService: PayTransparencyService,
              private videoInterviewFlowService: VideoInterviewFlowService) {
    // Both services are root-scoped and outlive a single flow, so clear them at the start of each
    // entry (this component is recreated per navigation into /initial-interview). Must run before the
    // entry page is chosen below.
    this.videoInterviewFlowService.reset();
    this.questionService.setCurrentQuestionNumber(0);
    this.route.queryParams.subscribe(params => {
      if (params['returningCandidate']) {
        this.returningCandidate = true;
        // PageService is root-scoped too, and unlike the two services cleared above it was never
        // reset here, so a previous flow's page - the video landing page among them - stayed active
        // until handleNavigation replaced it, and could paint whenever the posting arrived before the
        // pay-transparency data did. Clear it rather than choose one: this branch's entry page is
        // handleNavigation's to pick, so until then the body stays blank, as it does on a fresh load.
        // (The application-submit page the other branches start on is not neutral here: its buttons
        // record activity and send the do-this-later e-mail.)
        this.pageService.setActivePage(null);
        // handleNavigation can send the talent into the video step, so it must not run on video
        // interview fields that were read before this talent could be identified (BIZ-41058).
        if (this.payTransparencyService.getPayTransparencyData() != null) {
          const payTransparencyData = this.payTransparencyService.getPayTransparencyData();
          this.refreshVideoInterviewFields().subscribe(() => this.handleNavigation(payTransparencyData));
        } else {
          this.initialInterviewApiService.readTalentPayTransparencyData(
            this.talentHappyPathDataService.talent.personId,
            this.talentHappyPathDataService.postingInfo.orderId
          ).subscribe(data => {
            this.payTransparencyService.setPayTransparencyData(data);
            this.refreshVideoInterviewFields().subscribe(() => this.handleNavigation(data));
          });
        }{}
      } else if (params['gatheruuid']) {
        this.gatherUUID = params['gatheruuid'];

        if (params['returningGather']) {
          this.returningGather = true;
        } else {
          this.pageService.setActivePage(InitialInterviewPage.APPLICATION_SUBMIT_PAGE);
        }
      } else {
        this.pageService.setActivePage(InitialInterviewPage.APPLICATION_SUBMIT_PAGE);
      }
    });
  }

  ngOnInit() {
    if (this.gatherUUID != undefined && this.talentHappyPathDataService.postingInfo == null) {
      // refetch posting info and gather info
      let postingId = String(this.route.snapshot.queryParams['postingId']);

      const postingInfoPromise = this.quickApplyService.readPostingInfo(postingId)
          .pipe(timeout(this.timeout_duration))
          .toPromise()
          .catch(error => {
            console.error('Error fetching posting info:', error);
            return null;
          });

      const gatherPromise = this.gatherApiService.getGatherByToken(this.gatherUUID)
          .pipe(timeout(this.timeout_duration))
          .toPromise()
          .catch(error => {
            console.error('Error fetching posting info:', error);
            return null;
          });

      Promise.all([postingInfoPromise, gatherPromise])
          .then(([postingInfo, gatherInfo]) => {
if (postingInfo == null || gatherInfo == null) {
  // Nothing to render without these, and reading them anyway left the page blank (BIZ-40873).
  // If postingId is missing/invalid, fall back to a route that doesn't require it.
  const hasPostingId = postingId && postingId !== 'undefined' && postingId !== 'null';
  void this.router.navigate(hasPostingId ? ['quick-apply', postingId] : ['/myaquent'], { skipLocationChange: true });
  return;
}

            this.sharedDataService.setGather(gatherInfo);
            this.talentHappyPathDataService.postingInfo = postingInfo;
            this.talentHappyPathDataService.postingId = postingInfo.jobPostingId;

            this.talentHappyPathDataService.gather = gatherInfo;
            this.talentHappyPathDataService.talent = {
              email: gatherInfo.talentEmail,
              personId:gatherInfo.talentPersonId,
              firstName: gatherInfo.talent?.firstName,
              lastName: gatherInfo.talent?.lastName,
              preferredName: gatherInfo.talent?.preferredName,
              personTypeId: undefined,
              profileUpdated: undefined,
              resumeUpdated: undefined,
              resumeData: undefined,
              termsAccepted: undefined,
              locationUpdated: undefined,
              smsOptIn: undefined,
              doubleOptIn: undefined,
              newTHPTalent: undefined,
              geoLocation: undefined,
              portfolioUpdated: undefined
            };

            this.checkStandardQuestions();

            if (this.returningGather) {
              this.pageService.setActivePage(InitialInterviewPage.WELCOME_BACK_PAGE);
            }
          });
    } else {
      this.postingInfo = this.talentHappyPathDataService.postingInfo;
      if (this.talentHappyPathDataService.postingInfo.countryId === Country.France) {
        this.router.navigateByUrl('/talent-profile', {skipLocationChange: true});
      } else {
        this.refreshVideoInterviewFields().subscribe(() => this.checkStandardQuestions());
      }
    }
    this.questionService.currentQuestions$.subscribe(questionNumber => {
      this.currentQuestionIndex = questionNumber;
      this.refreshStandardQuestionIds();
    });
    this.pageService.activePage$.subscribe(page => {
      this.activePage = page
    });
  }

  checkStandardQuestions() {
    this.posting = this.talentHappyPathDataService.postingInfo;
    const personId = this.talentHappyPathDataService.talent.personId;
    const orderId = this.posting.orderId;

    if (!this.returningCandidate && !this.returningGather) {
      // The text is only used when the placeholder replacement feature is off.
      let standardQuestions = this.translateService.instant('talent-happy-path.initial-interview-questions.standard-question');
      this.initialInterviewApiService.addStandardInterviewQuestion(personId, orderId, standardQuestions).subscribe((questions) => {
        this.questionsLoading = true;
        this.initialInterviewQuestions = this.mapStandardQuestionsToInitialInterviewQuestions(questions);
        this.refreshStandardQuestionIds();
        this.checkForQuestions();
      }, error => {
        console.error('Error adding standard question:', error);
        this.pageService.setActivePage(InitialInterviewPage.HANG_TIGHT_PAGE);
        this.checkForQuestions();
      });
    }
  }

  checkForQuestions(): void {
    const preInterviewQuestionPromise = this.preInterviewQuestionsService
      .getQuestionsAndAnswersForPostingId(this.posting.jobPostingId, this.talentHappyPathDataService.talent.personId)
      .pipe(timeout(this.timeout_duration))
      .toPromise()
      .catch(error => {
        console.error('Error fetching pre-interview questions:', error);
        return [];
      });

    const customQuestionsPromise = this.initialInterviewApiService
      .provideCustomQuestions(this.posting.jobPostingId, this.talentHappyPathDataService.talent.personId)
      .pipe(timeout(this.timeout_duration))
      .toPromise()
      .catch(error => {
        console.error('Error fetching custom questions:', error);
        return [];
      });

    Promise.all([customQuestionsPromise, preInterviewQuestionPromise])
      .then(([customQuestions, preInterviewQuestions]) => {
        let combinedQuestions = combineQuestions(preInterviewQuestions, customQuestions);
        // The placeholder slot is held locally and also comes back from /provide.
        const alreadyShowing = new Set(this.initialInterviewQuestions
          .map(question => `${question.initialInterviewQuestionType}:${question.questionId}`));
        combinedQuestions = combinedQuestions.filter(question =>
          !alreadyShowing.has(`${question.initialInterviewQuestionType}:${question.questionId}`));
        this.initialInterviewQuestions = [...this.initialInterviewQuestions, ...combinedQuestions];
        this.refreshStandardQuestionIds();
        this.questionsLoading = false;
        if (combinedQuestions.length == 0) {
          if (this.pageService.getActivePage() === InitialInterviewPage.HANG_TIGHT_PAGE) {
            // Leaving hang tight is also a Workflow B entry point (BIZ-41038).
            this.enterVideoInterviewOr(InitialInterviewPage.NO_QUESTIONS_PAGE);
          }
        } else if (this.pageService.getActivePage() === InitialInterviewPage.HANG_TIGHT_PAGE) {
          // Resume after whichever question they were waiting on, which is not always the first.
          this.questionService.setCurrentQuestionNumber(this.currentQuestionIndex + 1);
          // Leaving hang tight is also a Workflow B entry point (BIZ-41038).
          this.enterVideoInterviewOr(InitialInterviewPage.QUESTIONS_PAGE);
        }
      });
  }

  provideQuestions(questions: InitialInterviewQuestion[]) {
    this.initialInterviewQuestions = questions;
    this.refreshStandardQuestionIds();
  }

  mapStandardQuestionsToInitialInterviewQuestions(customQuestions: CustomQuestion[]): InitialInterviewQuestion[] {
    return (customQuestions ?? []).map(customQuestion => ({
      questionId: customQuestion.questionId,
      question: customQuestion.question,
      typeId: customQuestion.typeId,
      submitted: customQuestion.submitted,
      answer: customQuestion.answer,
      initialInterviewQuestionType: InitialInterviewQuestionType.CUSTOM_QUESTION,
      optionChoiceList: null,
      isStandardQuestion: true
    }));
  }

  /**
   * Only the questions the talent has reached. They can land on the no-questions page part way through
   * the placeholder slot, and submitting the rest would mark unseen questions answered.
   *
   * <p>Held in a field because binding a method that builds a new array trips checkNoChanges.
   */
  private refreshStandardQuestionIds() {
    this.standardQuestionIds = (this.initialInterviewQuestions ?? [])
      .filter((question, index) => question.isStandardQuestion && index <= this.currentQuestionIndex)
      .map(question => question.questionId);
  }

  /**
   * Under Workflow B the video interview comes first and resumes to the given IIQ page afterward;
   * otherwise go straight to that page. Callers: a returning talent's entry past pay transparency
   * (handleNavigation) and welcome back's Continue (both the splash page), and the way out of hang
   * tight (BIZ-41038): the "Let's do this" page that normally makes this decision was replaced by
   * hang tight when the standard question call failed, so the decision is made here instead.
   */
  enterVideoInterviewOr(page: InitialInterviewPage): void {
    if (this.videoInterviewFlowService.shouldShowForWorkflowB()) {
      this.videoInterviewFlowService.enterVideoInterview(page);
    } else {
      this.pageService.setActivePage(page);
    }
  }

  handleNavigation(data: OrderPayTransparencyData) {
    let minPayRate: number;
    let maxPayRate: number;
    if (data.payTransparencyCategoryId === PayTransparencyCategory.SALARY) {
      minPayRate = data.minSalary;
      maxPayRate = data.maxSalary;
    } else {
      minPayRate = data.minRate;
      maxPayRate = data.maxRate;
    }
    if (this.featureService.getFeature(TalentAppFeatures.APPLICANT_PAY_TRANSPARENCY_FEATURE) && !isNumbersEqual(minPayRate, maxPayRate) && this.talentHappyPathDataService.postingInfo?.countryId != Country.Germany) {
      this.pageService.setActivePage(InitialInterviewPage.PAY_TRANSPARENCY);
    } else {
      this.enterVideoInterviewOr(InitialInterviewPage.SPLASH_PAGE);
    }
  }

  /**
   * Re-reads the two video interview fields from the posting endpoint, preserving the rest of the
   * existing postingInfo (e.g. the gather-specific order id). Returns an observable that emits once
   * the read has settled — with the fields set, or cleared if it failed or ran out of time — so a
   * caller can wait before making a Workflow B decision.
   *
   * Two reasons it re-reads rather than trusting what postingInfo already holds. The gather flow
   * pre-populates postingInfo (in the availability page) without these fields at all, bypassing the
   * refetch above. And since BIZ-41058 the fields depend on *who is asking*: the test cohort decides
   * them while it is switched on, the server takes the person from the credential the request
   * carries rather than from anything the request says, and the first read of a posting happens on
   * the job advert before anyone has signed in. A value read then says "we could not name you",
   * which under the cohort means no step — so a returning applicant who has since signed in has to
   * ask again to be offered the step at all.
   *
   * Read with `authenticated: true` everywhere except on a gather link. That is what offers the
   * MyAquent portal credential, which `ApiService` attaches only on request; the funnel's own token
   * travels either way through the interceptor in `QuickApplyModule`, and replaces the portal one
   * when both exist, so this matters only for a talent with no funnel sign-in. On a gather link it
   * is deliberately not offered: the gather link alone does not name the talent, so while the test
   * cohort is in force it does not earn them the step (BIZ-41058, the video interview test cohort),
   * and a portal token left in the same browser can belong to somebody else. A funnel sign-in still
   * travels through the interceptor whatever this says, so a browser signed in to the funnel is
   * named by that sign-in here too — the embed doc's section 4 covers what then happens.
   *
   * Shared and read once per entry into this module: every caller here wants the same answer, and
   * a second read landing after a page had been picked could change the fields under a decision
   * already taken.
   *
   * A failed read CLEARS both fields rather than leaving what was there. `readPostingInfo` turns an
   * HTTP error into null, and what postingInfo already holds was read for whoever was asking at the
   * time — on a second entry into this module that can be an enabled value read for an identity we
   * can no longer confirm. The gate exists so the step cannot appear by accident, so "we could not
   * find out" has to mean no step, which is how `getWorkflow` reads an undefined field.
   */
  private refreshVideoInterviewFields(): Observable<unknown> {
    if (this.videoInterviewFields) {
      return this.videoInterviewFields;
    }
    const posting = this.talentHappyPathDataService.postingInfo;
    if (!posting || posting.jobPostingId == null) {
      return of(null);
    }
    const offerThePortalCredential = this.gatherUUID == undefined;
    this.videoInterviewFields = this.quickApplyService.readPostingInfo(String(posting.jobPostingId), offerThePortalCredential).pipe(
      // A read cancelled because the entry ended writes nothing and wakes no waiter; the next entry
      // re-reads the fields before anything uses them.
      takeUntil(this.destroyed),
      timeout(this.videoInterviewFieldsTimeoutMs),
      map(fresh => {
        // Unconditional, including when fresh is null: see the note above on failing closed.
        posting.videoInterviewEnabled = fresh?.videoInterviewEnabled;
        posting.videoInterviewWorkflow = fresh?.videoInterviewWorkflow;
      }),
      catchError(error => {
        // readPostingInfo already turns an HTTP error into null, so what arrives here is the
        // timeout above, or anything else that escapes it - and it has to clear the fields too,
        // or it would be the one path that leaves a stale enabled value behind.
        console.error('Error fetching video interview posting info:', error);
        posting.videoInterviewEnabled = undefined;
        posting.videoInterviewWorkflow = undefined;
        return of(null);
      }),
      shareReplay(1)
    );
    return this.videoInterviewFields;
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  protected readonly InitialInterviewPage = InitialInterviewPage;
}
